import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import SlideshowPlayer, { ISlideshowPlayerProps } from './SlideshowPlayer';

export interface ISlideshowPlayerTickerProps extends Omit<ISlideshowPlayerProps, 'currentTime'> {
    /**
     * Controls playback. When false (default) the clock is paused at its current position.
     * Toggle to true to start/resume, false to pause.
     */
    isPlaying?: boolean;
}

/**
 * Imperative handle exposed via ref, for cases where the caller needs to control
 * the playback position directly (scrub bar, audio sync, keyboard seek).
 */
export interface ISlideshowPlayerTickerHandle {
    /** Jump playback to an arbitrary position without interrupting the RAF loop. */
    seek: (timeMs: number) => void;
}

/**
 * Always prefer this component over using SlideshowPlayer directly.
 *
 * SlideshowPlayerTicker owns the playback clock internally, driven by
 * requestAnimationFrame (RAF) — a browser API that fires just before each display
 * repaint (~60fps), syncs to the screen's refresh rate, and automatically pauses
 * when the tab is hidden. This means the rapid per-frame state updates stay
 * isolated inside this subtree: the surrounding UI (editor panels, settings, slide
 * thumbnails, etc.) does not re-render on every animation frame.
 *
 * For cases where the caller needs to control the playback position directly
 * (editor scrub bar, audio sync, seeking to a specific frame), obtain a ref to
 * this component and call `ref.current.seek(milliseconds)`. The ticker remains the
 * single owner of currentTime in all cases.
 *
 * Use the raw SlideshowPlayer only when you need a fully stateless renderer —
 * for example, rendering a frozen preview frame at a fixed time.
 */
const SlideshowPlayerTicker = forwardRef<ISlideshowPlayerTickerHandle, ISlideshowPlayerTickerProps>(
    ({ isPlaying = false, slideshow, onTimeChange, ...rest }, ref) => {
        const [currentTime, setCurrentTime] = useState(0);
        const rafRef = useRef<number | null>(null);
        const lastTimestampRef = useRef<number | null>(null);

        useImperativeHandle(ref, () => ({
            seek: (timeMs: number) => {
                // Reset the delta reference so the next RAF tick doesn't treat
                // the gap between the seek point and the previous frame as elapsed time.
                lastTimestampRef.current = null;
                setCurrentTime(timeMs);
            }
        }));

        // Reset the clock whenever the slideshow is replaced (e.g. slide count changes)
        useEffect(() => {
            lastTimestampRef.current = null;
            setCurrentTime(0);
        }, [slideshow]);

        useEffect(() => {
            if (!isPlaying) {
                if (rafRef.current !== null) {
                    cancelAnimationFrame(rafRef.current);
                    rafRef.current = null;
                }

                lastTimestampRef.current = null;
                return;
            }

            let cancelled = false;
            // Reset so the first tick doesn't compute a delta from a stale timestamp
            // (mirrors the same guard in seek()). Fixes resume-from-wrong-position.
            lastTimestampRef.current = null;

            const tick = (timestamp: number) => {
                // If the effect was cleaned up before this RAF fired, bail out
                // without scheduling the next frame (prevents the RAF loop from
                // continuing to run after a pause).
                if (cancelled) {
                    return;
                }

                if (lastTimestampRef.current !== null) {
                    const deltaMs = timestamp - lastTimestampRef.current;
                    setCurrentTime((t) => t + deltaMs);
                }

                lastTimestampRef.current = timestamp;
                rafRef.current = requestAnimationFrame(tick);
            };

            rafRef.current = requestAnimationFrame(tick);

            return () => {
                cancelled = true;

                if (rafRef.current !== null) {
                    cancelAnimationFrame(rafRef.current);
                    rafRef.current = null;
                }

                lastTimestampRef.current = null;
            };
        }, [isPlaying]);

        useEffect(() => {
            onTimeChange?.(currentTime);
        }, [currentTime, onTimeChange]);

        return <SlideshowPlayer slideshow={slideshow} currentTime={currentTime} {...rest} />;
    }
);

SlideshowPlayerTicker.displayName = 'SlideshowPlayerTicker';

export default SlideshowPlayerTicker;
